import { useTranslation } from "react-i18next";
import { DetailSection, EntityHeader, EntityChipList, type EntityLookup } from "./detailParts";
import type { OntologyEntity } from "./types";
import { entityDisplayName } from "./types";
import Chip from "../../../ui/feedback/Chip";

interface OntologyEntityDetailsProps {
  entity: OntologyEntity | null;
  lookup: EntityLookup;
}

function LocalizedList({ values, emptyText }: { values: OntologyEntity["label"]; emptyText: string }) {
  if (values.length === 0) {
    return <p className="app-text-muted text-sm">{emptyText}</p>;
  }
  return (
    <div className="space-y-1">
      {values.map((item, index) => (
        <div key={`${item.value}-${item.language ?? "none"}-${String(index)}`} className="app-text text-sm">
          <span>{item.value}</span>
          {item.language ? <Chip className="ml-2">{item.language}</Chip> : null}
        </div>
      ))}
    </div>
  );
}

/** What the viewer's model knows about an entry: used for the roots of the trees, which
 * are no terms of the version and so have no term details. */
export default function OntologyEntityDetails({ entity, lookup }: OntologyEntityDetailsProps) {
  const { t } = useTranslation();
  if (!entity) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <p className="app-text-muted text-sm">{t("ontologyViewer.noEntitySelected")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <EntityHeader name={entityDisplayName(entity)} iri={entity.iri} />
      <DetailSection title={t("ontologyViewer.labels")}>
        <LocalizedList values={entity.label} emptyText={t("ontologyViewer.noLabels")} />
      </DetailSection>
      <DetailSection title={t("ontologyViewer.definitions")}>
        <LocalizedList values={entity.definition} emptyText={t("ontologyViewer.noDefinitions")} />
      </DetailSection>
      <DetailSection title={t("ontologyViewer.parents")}>
        <EntityChipList values={entity.parents} lookup={lookup} emptyText={t("ontologyViewer.noParents")} />
      </DetailSection>
      <DetailSection title={t("ontologyViewer.children")}>
        <EntityChipList values={entity.children} lookup={lookup} emptyText={t("ontologyViewer.noChildren")} />
      </DetailSection>
    </div>
  );
}
